/**
 * AdminBetaIntelligence · Feb 2026
 *
 * Soulumo's Beta Intelligence Dashboard. Not a list — a map.
 * Answers Michael's 7 "first beta review output" questions at a
 * glance:
 *
 *   WHAT PEOPLE LOVE              → Experience performance table (top by shift/overall)
 *   WHAT ISN'T WORKING            → Diagnostics (blocker frequencies)
 *   WHAT MAKES SOULUMO DIFFERENT  → Topline differentiation + comments
 *   BIGGEST EMOTIONAL SHIFT       → Topline + per-installation shift ranking
 *   WHAT MAKES PEOPLE RETURN      → Return-intent topline + repeat sessions
 *   WHAT WE SHOULD FIX NEXT       → RAG signals
 *   WHAT USERS ARE SAYING         → Testimonial library + raw events
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "../AdminPages";
import {
  ArrowLeft, Loader2, MessageSquare, Trash2, Check, Copy, Mail,
  TrendingUp, AlertCircle, Sparkles, BarChart3,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` });

const METRIC_LABEL = {
  overall_experience: "Overall",
  emotional_shift:    "Emotional Shift",
  personal_relevance: "Personal Relevance",
  immersion:          "Immersion",
  voice:              "Voice",
  ease:               "Ease",
  return_intent:      "Return Intent",
  recommendation:     "Recommendation",
  differentiation:    "Differentiation",
  state_shift:        "State Shift (post-session)",
};

const DISPLAY_ORDER = [
  "overall_experience", "emotional_shift", "state_shift", "differentiation",
  "return_intent", "recommendation", "personal_relevance", "immersion", "voice", "ease",
];

export default function AdminBetaIntelligence() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview"); // overview | performance | testimonials | diagnostics | events
  const [eventTypeFilter, setEventTypeFilter] = useState("");
  const [testimonialFilter, setTestimonialFilter] = useState("all");
  const [copied, setCopied] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const [a, b, c] = await Promise.all([
        fetch(`${API}/api/admin/beta-intelligence`, { headers: auth() }),
        fetch(`${API}/api/admin/beta-intelligence/testimonials`, { headers: auth() }),
        fetch(`${API}/api/admin/beta-intelligence/events`, { headers: auth() }),
      ]);
      if (!a.ok) throw new Error(`intelligence HTTP ${a.status}`);
      setData(await a.json());
      setTestimonials((await b.json()).items || []);
      setEvents((await c.json()).items || []);
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const patch = async (id, body) => {
    try {
      await fetch(`${API}/api/admin/beta-intelligence/events/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify(body),
      });
      await load();
    } catch (e) { setErr(String(e.message || e)); }
  };

  const removeEvent = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await fetch(`${API}/api/admin/beta-intelligence/events/${id}`, { method: "DELETE", headers: auth() });
      await load();
    } catch (e) { setErr(String(e.message || e)); }
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key); setTimeout(() => setCopied(null), 1500);
    } catch (_) {}
  };

  const filteredTestimonials = useMemo(() => {
    if (testimonialFilter === "all") return testimonials;
    if (testimonialFilter === "approved") return testimonials.filter((t) => t.approved);
    if (testimonialFilter === "anonymous") return testimonials.filter((t) => t.permission === "anonymous");
    if (testimonialFilter === "team_only") return testimonials.filter((t) => t.permission === "team_only");
    return testimonials;
  }, [testimonials, testimonialFilter]);

  const filteredEvents = useMemo(() => {
    if (!eventTypeFilter) return events;
    return events.filter((e) => e.type === eventTypeFilter);
  }, [events, eventTypeFilter]);

  return (
    <AdminLayout title="Beta Intelligence">
      <div className="mb-5 flex items-center justify-between" data-testid="admin-beta-intelligence">
        <button
          type="button"
          onClick={() => nav("/admin")}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900"
          data-testid="bi-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to admin
        </button>
        <button
          type="button"
          onClick={load}
          className="text-sm text-slate-500 hover:text-slate-900"
          data-testid="bi-refresh"
        >
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="mb-5 flex flex-wrap gap-2" data-testid="bi-tabs">
        {[
          ["overview",     "Overview",      BarChart3],
          ["performance",  "Installations", TrendingUp],
          ["testimonials", `Testimonials (${(data && data.testimonial_count) || 0})`, MessageSquare],
          ["diagnostics",  "Diagnostics",   AlertCircle],
          ["events",       `All events (${events.length})`, Sparkles],
        ].map(([k, label, Icon]) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            data-testid={`bi-tab-${k}`}
            className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border transition ${
              tab === k
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : err ? (
        <div className="text-sm text-red-600" data-testid="bi-error">{err}</div>
      ) : (
        <>
          {tab === "overview" && <OverviewPanel data={data} />}
          {tab === "performance" && <PerformancePanel data={data} />}
          {tab === "testimonials" && (
            <TestimonialPanel
              items={filteredTestimonials}
              filter={testimonialFilter}
              setFilter={setTestimonialFilter}
              onApprove={(id, approved) => patch(id, { testimonial_approved: approved })}
              onCopy={copy}
              copied={copied}
            />
          )}
          {tab === "diagnostics" && <DiagnosticsPanel data={data} />}
          {tab === "events" && (
            <EventsPanel
              items={filteredEvents}
              typeFilter={eventTypeFilter}
              setTypeFilter={setEventTypeFilter}
              onRemove={removeEvent}
            />
          )}
        </>
      )}
    </AdminLayout>
  );
}

// ──────────────────────────────────────────────────────────────────
function OverviewPanel({ data }) {
  if (!data) return null;
  const topline = data.topline || {};
  const beh = data.behaviour || {};
  const signals = data.signals || [];

  return (
    <div className="space-y-6" data-testid="bi-overview">
      {/* Topline metric grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="bi-topline">
        {DISPLAY_ORDER.map((k) => (
          <MetricTile key={k} label={METRIC_LABEL[k]} value={topline[k]} metricKey={k} />
        ))}
        <NpsTile nps={data.nps} />
      </div>

      {/* Behaviour row */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">Behaviour</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 text-sm">
          <BehRow label="Testers" value={beh.total_testers} />
          <BehRow label="Total sessions" value={beh.total_sessions} />
          <BehRow label="Avg sessions / tester" value={beh.avg_sessions_per_tester} />
          <BehRow label="Pulse responses" value={beh.pulse_responses} />
          <BehRow label="State-shift responses" value={beh.state_shift_responses} />
          <BehRow label="Micro responses" value={beh.micro_responses} />
          <BehRow label="Testimonials" value={beh.testimonial_count} />
          <BehRow label="Shareable testimonials" value={beh.testimonial_shareable} />
        </div>
      </div>

      {/* Signals */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="text-xs uppercase tracking-widest text-slate-400 mb-3">Signals</div>
        {signals.length === 0 ? (
          <div className="text-sm text-slate-500 italic">
            Quiet — no red or green flags yet. They'll appear here as beta signal grows.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="bi-signals">
            {signals.map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span className={`inline-block w-2.5 h-2.5 mt-1.5 rounded-full flex-shrink-0 ${
                  s.severity === "red" ? "bg-red-500"
                    : s.severity === "orange" ? "bg-orange-400"
                      : "bg-emerald-500"
                }`} />
                <span className="text-slate-700">{s.label}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function MetricTile({ label, value, metricKey }) {
  const color = useMemo(() => {
    if (value == null) return "text-slate-300";
    if (value >= 8) return "text-emerald-600";
    if (value >= 6) return "text-amber-600";
    return "text-red-600";
  }, [value]);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`bi-metric-${metricKey}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{label}</div>
      <div className={`text-3xl font-bold mt-1 ${color}`}>
        {value == null ? "—" : value.toFixed(1)}
      </div>
      <div className="text-xs text-slate-400">of 10</div>
    </div>
  );
}

function NpsTile({ nps }) {
  const color =
    nps == null ? "text-slate-300"
      : nps >= 50 ? "text-emerald-600"
        : nps >= 0 ? "text-amber-600"
          : "text-red-600";
  return (
    <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-4" data-testid="bi-metric-nps">
      <div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold">NPS</div>
      <div className={`text-3xl font-bold mt-1 ${color}`}>
        {nps == null ? "—" : (nps > 0 ? `+${nps}` : nps)}
      </div>
      <div className="text-xs text-amber-700">from recommendation</div>
    </div>
  );
}

function BehRow({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{label}</div>
      <div className="text-xl font-bold text-slate-900 mt-0.5">
        {value == null ? "—" : value}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
function PerformancePanel({ data }) {
  const rows = (data && data.experience_performance) || [];
  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-sm text-slate-500 text-center">
        No installation-level data yet.
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="bi-performance">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-widest">
            <tr>
              <th className="text-left px-4 py-3">Installation</th>
              <th className="text-right px-3 py-3">Sessions</th>
              <th className="text-right px-3 py-3">Overall</th>
              <th className="text-right px-3 py-3">Shift</th>
              <th className="text-right px-3 py-3">Immersion</th>
              <th className="text-right px-3 py-3">Completion %</th>
              <th className="text-right px-3 py-3">Testimonials</th>
              <th className="text-right px-4 py-3">Low scores</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.installation_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`bi-perf-${r.installation_id}`}>
                <td className="px-4 py-3 text-slate-900 font-medium">{r.installation_name}</td>
                <td className="text-right px-3 py-3 text-slate-600">{r.sessions}</td>
                <td className={`text-right px-3 py-3 font-semibold ${scoreColor(r.overall_avg)}`}>
                  {r.overall_avg != null ? r.overall_avg : "—"}
                </td>
                <td className={`text-right px-3 py-3 font-semibold ${scoreColor(r.shift_avg)}`}>
                  {r.shift_avg != null ? r.shift_avg : "—"}
                </td>
                <td className={`text-right px-3 py-3 ${scoreColor(r.immersion_avg)}`}>
                  {r.immersion_avg != null ? r.immersion_avg : "—"}
                </td>
                <td className="text-right px-3 py-3 text-slate-600">
                  {r.completion_avg != null ? `${r.completion_avg}%` : "—"}
                </td>
                <td className="text-right px-3 py-3 text-slate-600">{r.testimonials}</td>
                <td className="text-right px-4 py-3">
                  {r.detractor_hits > 0 ? (
                    <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200">
                      {r.detractor_hits}
                    </span>
                  ) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function scoreColor(v) {
  if (v == null) return "text-slate-400";
  if (v >= 8) return "text-emerald-600";
  if (v >= 6) return "text-amber-600";
  return "text-red-600";
}

// ──────────────────────────────────────────────────────────────────
function TestimonialPanel({ items, filter, setFilter, onApprove, onCopy, copied }) {
  const chips = [
    ["all", "All"], ["approved", "Approved"], ["anonymous", "Anonymous"], ["team_only", "Team only"],
  ];
  return (
    <div className="space-y-4" data-testid="bi-testimonials">
      <div className="flex flex-wrap gap-2">
        {chips.map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setFilter(k)}
            data-testid={`bi-testimonial-filter-${k}`}
            className={`text-xs px-3 py-1.5 rounded-full border ${
              filter === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-sm text-slate-500 text-center">
          No testimonials in this filter yet.
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((t) => {
            const quote = `"${t.testimonial}" — ${(t.name || "").split(" ")[0] || "Soulumo tester"}`;
            const score = (t.scores || {}).overall_experience;
            return (
              <div key={t.id} className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-4" data-testid={`bi-testimonial-${t.id}`}>
                <blockquote className="text-slate-800 italic font-medium text-base leading-relaxed">
                  "{t.testimonial}"
                </blockquote>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                  <strong className="text-slate-700">{t.name || "Anonymous"}</strong>
                  {t.context && t.context.installation_name && (
                    <>· <span>{t.context.installation_name}</span></>
                  )}
                  {score != null && <>· <span>Pulse: {score}/10</span></>}
                  {(t.scores || {}).emotional_shift != null && (
                    <>· <span>Shift: {t.scores.emotional_shift}/10</span></>
                  )}
                  <>· <span>{new Date(t.created_at).toLocaleDateString()}</span></>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                    t.permission === "name" ? "bg-emerald-100 text-emerald-700"
                      : t.permission === "anonymous" ? "bg-sky-100 text-sky-700"
                        : "bg-slate-200 text-slate-600"
                  }`}>
                    {t.permission === "name" ? "✓ public with first name"
                      : t.permission === "anonymous" ? "✓ public anonymously"
                        : "team-only"}
                  </span>
                  {(t.permission === "name" || t.permission === "anonymous") && (
                    <button
                      type="button"
                      onClick={() => onApprove(t.id, !t.approved)}
                      className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${
                        t.approved
                          ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                      }`}
                      data-testid={`bi-testimonial-approve-${t.id}`}
                    >
                      <Check className="w-3 h-3" />
                      {t.approved ? "Approved" : "Approve for public use"}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onCopy(quote, `tc-${t.id}`)}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-50"
                    data-testid={`bi-testimonial-copy-${t.id}`}
                  >
                    {copied === `tc-${t.id}` ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                    Copy quote
                  </button>
                  <a
                    href={`mailto:${t.email}?subject=${encodeURIComponent("Thank you for the Soulumo testimonial")}`}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-slate-900 text-white hover:bg-slate-800"
                  >
                    <Mail className="w-3 h-3" /> Thank them
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
function DiagnosticsPanel({ data }) {
  const diag = (data && data.diagnostics) || { blockers: [], detractor_pulse_count: 0 };
  return (
    <div className="space-y-4" data-testid="bi-diagnostics">
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">
          What's getting in the way?
        </div>
        <p className="text-sm text-slate-600 mb-4">
          From {diag.detractor_pulse_count} pulse responses scoring 0–5 overall. Reasons they chose.
        </p>
        {diag.blockers.length === 0 ? (
          <div className="text-sm text-slate-500 italic">
            No detractor blocker data yet.
          </div>
        ) : (
          <div className="space-y-2">
            {diag.blockers.map((b) => (
              <div key={b.label} className="flex items-center gap-3" data-testid={`bi-blocker-${b.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="flex-1 text-sm text-slate-700">{b.label}</div>
                <div className="w-32 bg-slate-100 rounded-full h-2 overflow-hidden">
                  <div className="bg-red-400 h-full" style={{ width: `${b.pct}%` }} />
                </div>
                <div className="w-14 text-right text-sm text-slate-600 font-medium">{b.pct}%</div>
                <div className="w-10 text-right text-xs text-slate-400">({b.count})</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
function EventsPanel({ items, typeFilter, setTypeFilter, onRemove }) {
  return (
    <div className="space-y-3" data-testid="bi-events">
      <div className="flex flex-wrap gap-2">
        {["", "pulse", "state_shift", "micro", "miss_most"].map((t) => (
          <button
            key={t || "all"}
            type="button"
            onClick={() => setTypeFilter(t)}
            data-testid={`bi-event-filter-${t || "all"}`}
            className={`text-xs px-3 py-1.5 rounded-full border ${
              typeFilter === t ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
            }`}
          >
            {t ? t.replace("_", " ") : "All"}
          </button>
        ))}
      </div>
      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-sm text-slate-500 text-center">
          No events in this filter yet.
        </div>
      ) : items.map((e) => <EventRow key={e.id} event={e} onRemove={onRemove} />)}
    </div>
  );
}

function EventRow({ event, onRemove }) {
  const [open, setOpen] = useState(false);
  const scores = event.scores || {};
  const ctx = event.context || {};
  const t = event.testimonial || {};
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`bi-event-${event.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 capitalize">
              {event.type.replace("_", " ")}
            </span>
            <span className="font-semibold text-slate-900">{event.name || event.email}</span>
            {ctx.installation_name && (
              <span className="text-xs text-slate-500">· {ctx.installation_name}</span>
            )}
          </div>
          <div className="text-xs text-slate-400 mt-0.5">
            {new Date(event.created_at).toLocaleString()}
            {ctx.session_number && ` · session #${ctx.session_number}`}
            {ctx.voice_label && ` · voice: ${ctx.voice_label}`}
          </div>
        </div>
        <button
          type="button"
          onClick={() => onRemove(event.id)}
          className="text-slate-400 hover:text-red-600"
          data-testid={`bi-event-remove-${event.id}`}
          aria-label="Delete event"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-xs text-slate-500 mt-2 underline"
      >
        {open ? "Hide details" : "Show details"}
      </button>
      {open && (
        <div className="mt-3 text-xs text-slate-600 space-y-1 bg-slate-50 border border-slate-100 rounded-lg p-3">
          <pre className="whitespace-pre-wrap break-words font-mono text-[11px]">
            {JSON.stringify({ scores, answers: event.answers, testimonial: t, context: ctx }, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
